import { memo } from 'react'
import { Badge, CopyButton, Group, ScrollArea, SimpleGrid, Stack, Text, UnstyledButton } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { TbArrowRight, TbCheck, TbCopy } from 'react-icons/tb'
import { PROBLEM_COLORS } from './problemColors'
import type { ProblemType, RouteProblem } from './routeProblems'

const COPYABLE_ID_STYLE = { display: 'inline-flex', alignItems: 'center', gap: 6 }

type CopyLabels = { copy: string; copied: string }

// A lighter `CopyableValue`: a Tooltip, nested layout and translation hooks per entry make expanding long lists slow
const CopyableId = ({ value, labels }: { value: string; labels: CopyLabels }) => {
  return (
    <CopyButton value={value} timeout={1500}>
      {({ copied, copy }) => (
        <UnstyledButton
          onClick={copy}
          title={copied ? labels.copied : labels.copy}
          aria-label={`${labels.copy} ${value}`}
          ff="monospace"
          fz="sm"
          style={COPYABLE_ID_STYLE}
        >
          {value}
          {copied ? (
            <TbCheck size={14} color="var(--mantine-color-teal-6)" />
          ) : (
            <TbCopy size={14} color="var(--mantine-color-dimmed)" />
          )}
        </UnstyledButton>
      )}
    </CopyButton>
  )
}

const PositionBadge = ({ label }: { label: string | null }) => {
  if (label === null) return null
  return (
    <Badge size="xs" variant="default" radius="sm" style={{ flexShrink: 0 }}>
      {label}
    </Badge>
  )
}

// Routes checked against a much newer map can list thousands of entries, rendering them all freezes the page
export const MAX_DETAIL_ENTRIES = 50

const ProblemEntries = ({ problem, type }: { problem: RouteProblem; type: ProblemType }) => {
  const { t } = useTranslation(['routeProblems', 'common'])
  const labels: CopyLabels = { copy: t('copy', { ns: 'common' }), copied: t('copied', { ns: 'common' }) }
  const positionLabel = (position: number | null) => (position === null ? null : t('position', { position }))

  switch (type) {
    case 'missingSegments':
    case 'missingNodes':
      return problem[type].slice(0, MAX_DETAIL_ENTRIES).map(id => <CopyableId key={id} value={id} labels={labels} />)
    case 'oppositeDirectionSegments':
      return problem[type].slice(0, MAX_DETAIL_ENTRIES).map(({ segmentId, position }) => (
        <Group key={`${segmentId}-${position}`} gap="xs" wrap="nowrap">
          <PositionBadge label={positionLabel(position)} />
          <CopyableId value={segmentId} labels={labels} />
        </Group>
      ))
    case 'mismatchedEntranceNodes':
    case 'mismatchedExitNodes':
      return problem[type].slice(0, MAX_DETAIL_ENTRIES).map(({ nodeId, segmentId, position }) => (
        <Stack key={`${nodeId}-${segmentId}-${position}`} gap={2}>
          <Group gap="xs" wrap="nowrap">
            <PositionBadge label={positionLabel(position)} />
            <CopyableId value={nodeId} labels={labels} />
          </Group>
          {segmentId && (
            <Group gap="xs" wrap="nowrap" pl="md">
              <Text size="xs" c="dimmed">
                {t('onSegment')}
              </Text>
              <CopyableId value={segmentId} labels={labels} />
            </Group>
          )}
        </Stack>
      ))
    case 'disconnectedSegments':
      return problem[type].slice(0, MAX_DETAIL_ENTRIES).map(({ segmentId, nextSegmentId, position }) => (
        <Stack key={`${segmentId}-${nextSegmentId}-${position}`} gap={2}>
          <Group gap="xs" wrap="nowrap">
            <PositionBadge label={positionLabel(position)} />
            <CopyableId value={segmentId} labels={labels} />
          </Group>
          {nextSegmentId && (
            <Group gap="xs" wrap="nowrap" pl="md">
              <TbArrowRight size={14} aria-label={t('followedBy')} />
              <CopyableId value={nextSegmentId} labels={labels} />
            </Group>
          )}
        </Stack>
      ))
  }
}

const RouteProblemDetails = ({ problem, types }: { problem: RouteProblem; types: ProblemType[] }) => {
  const { t } = useTranslation('routeProblems')

  return (
    <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg" py="xs">
      {types.map(type => (
        <Stack key={type} gap="xs">
          <Group gap="xs">
            <Badge color={PROBLEM_COLORS[type]} variant="light">
              {problem[type].length}
            </Badge>
            <Text size="sm" fw={600}>
              {t(`types.${type}.label`)}
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            {t(`types.${type}.description`)}
          </Text>
          <ScrollArea.Autosize mah={240} type="auto">
            <Stack gap={4}>
              <ProblemEntries problem={problem} type={type} />
              {problem[type].length > MAX_DETAIL_ENTRIES && (
                <Text size="xs" c="dimmed">
                  {t('moreEntries', { count: problem[type].length - MAX_DETAIL_ENTRIES })}
                </Text>
              )}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>
      ))}
    </SimpleGrid>
  )
}

export default memo(RouteProblemDetails)
